import { IS_NATIVE } from '@/config/platform';

type Listener = () => void;

const listeners = new Set<Listener>();
let installed = false;

/// Prévient quand l'application revient au premier plan.
///
/// Sur le web et le bureau, « revenir » veut dire qu'un onglet redevient
/// visible : le code a continué de tourner pendant ce temps. Sur mobile, le
/// système **gèle** l'application — les minuteries s'arrêtent, les WebSocket
/// tombent, et rien ne prévient au réveil. Une application rouverte après une
/// heure affiche donc des chiffres d'il y a une heure, sans le dire.
///
/// D'où l'unification : `visibilitychange` d'un côté, `appStateChange` de
/// Capacitor de l'autre, une seule chose à écouter pour l'appelant.
export function onAppResume(listener: Listener): () => void {
  listeners.add(listener);
  install();
  return () => listeners.delete(listener);
}

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch (error) {
      // Un abonné qui échoue ne doit pas empêcher les autres d'être prévenus.
      console.error('[app-lifecycle] listener failed', error);
    }
  }
}

function install() {
  if (installed) return;
  installed = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') notify();
  });

  if (!IS_NATIVE) return;

  // `visibilitychange` existe aussi dans une WebView, mais il ne couvre pas
  // tous les retours : sur iOS notamment, revenir depuis le sélecteur
  // d'applications ne le déclenche pas toujours. `appStateChange` fait foi.
  void import('@capacitor/app').then(({ App }) => {
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) notify();
    });
  });
}
